import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../state/AppState';
import { getNextScreen, getScreenPath } from '../utils/smartNavigation';
import { createFindAnotherProviderHandler } from '../services/providerService';
import ProgressBar from '../components/ProgressBar';
import BackButton from '../components/BackButton';
import ProviderImage from '../components/ProviderImage';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';

/**
 * ScreenD - Visit type selection screen.
 *
 * Allows the user to select who the appointment is for (Myself or Someone Else).
 */
const ScreenD: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  
  // Only check for error after ALL loading steps are complete
  const hasProviderId = state.providerId && state.providerId.trim() !== '';
  const shouldShowError = !state.isLoading && state.loadingStep === 'complete' && !hasProviderId;
  
  // Default visitType to 'Myself' if not already set from continue request
  useEffect(() => {
    if (!state.visitType && !state.isLoading && state.loadingStep === 'complete') {
      dispatch({ type: 'SET_VISIT_TYPE', payload: 'Myself' });
    }
  }, [state.visitType, state.isLoading, state.loadingStep, dispatch]);
  
  // Get provider name from availability data
  const providerName = getProviderNameFromAvailability(state.availabilities, null);
  
  const handleContinue = () => {
    // Always set visitType to 'Myself' (default behavior)
    dispatch({ type: 'SET_VISIT_TYPE', payload: 'Myself' });
    
    // Create updated state with 'Myself' for navigation
    const updatedState = { ...state, visitType: 'Myself' };
    
    // Use centralized smart navigation with updated state
    const nextScreen = getNextScreen(updatedState);
    const path = getScreenPath(nextScreen);
    
    navigate(path);
  };

  const handleReturn = createFindAnotherProviderHandler(state.stateString || undefined);
  
  // Show loading state while global loading is still in progress
  if (state.isLoading) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <main className="mtg-screen-main" id="main-content">
            <div className="mtg-screen-card">
              <h2 className="mtg-screen-question">Loading...</h2>
              <p style={{ textAlign: 'center', marginBottom: '24px', color: '#333' }}>
                {state.loadingStep === 'session/continue' && 'Initializing session...'}
                {state.loadingStep === 'locations' && 'Loading locations...'}
                {state.loadingStep === 'availabilities' && 'Loading availability data...'}
                {state.loadingStep === 'session/all' && 'Finalizing session data...'}
                {state.loadingStep === 'error' && 'An error occurred while loading...'}
                {!state.loadingStep && 'Loading your appointment information...'}
              </p>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#e9ecef', 
                borderRadius: '4px', 
                overflow: 'hidden',
                margin: '20px 0'
              }}>
                <div style={{
                  width: `${state.loadingProgress}%`,
                  height: '100%',
                  backgroundColor: '#185F93',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <p style={{ textAlign: 'center', color: '#333', fontSize: '0.9rem', margin: '8px 0 0 0' }}>
                {state.loadingProgress}% complete
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (shouldShowError) {
    return (
      <div className="screen-bg">
        <div className="main-content-container">
          <main className="mtg-screen-main" id="main-content">
            <div className="mtg-screen-card">
              <h2 className="mtg-screen-question">Error</h2>
              <p style={{ textAlign: 'center', marginBottom: '24px', color: '#666' }}>
                Provider ID not found. Please return to mindfultherapygroup.com.
              </p>
              <div className="button-container">
                <button 
                  className="pill-btn-standard" 
                  onClick={handleReturn}
                  aria-label="Return to previous page"
                >
                  Return
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }


  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card">
            <h2 className="mtg-screen-question" style={{ marginBottom: 24 }}>Online Scheduling</h2>
            
            <div style={{ maxWidth: 280, margin: '0 auto', textAlign: 'left' }}>
              <ProviderImage
                maxWidth={280}
                maxHeight={280}
                borderRadius={24}
                style={{ marginBottom: 18 }}
              />
              
              <div style={{ 
                color: '#e69e6d', 
                fontSize: '1.25rem', 
                fontWeight: 600, 
                marginBottom: 18,
                textAlign: 'center'
              }}>
                {providerName}
              </div>
              
              <p style={{ 
                textAlign: 'left', 
                marginBottom: 24, 
                color: '#333',
                fontSize: '1rem',
                lineHeight: 1.5
              }}>
                To schedule your appointment,<br/>please click Continue and complete<br/>the following questions to reserve<br/>your time with {providerName}.
              </p>
              
              <div className="button-container" style={{ marginTop: 24 }}>
                <button 
                  className="pill-btn-standard" 
                  onClick={handleContinue}
                  aria-label="Continue to schedule appointment"
                >
                  Continue
                </button>
              </div>
            </div>
            
            <ProgressBar progress={0} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ScreenD; 